import { Info } from 'shared/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateTimeText } from '../../ui/Mono';
import ErrorCard from '../../ui/ErrorCard';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { GetAging } from './inventoryApi';

/**
 * FIFO / AGING (M9, item 4) — idade do pallet por data de criação do LPN (§6.2).
 *
 * ⚠️ FIFO É AVISO, NUNCA TRAVA. Esta tela não tem botão de bloquear, não marca
 * pallet como "proibido" e não impede nada. Ela ordena do mais velho para o
 * mais novo e diz a idade — quem decide furar a fila é a pessoa, sabendo. O
 * aviso está escrito no topo justamente para ninguém ler a lista como ordem.
 */

const FormatInt = (value) => new Intl.NumberFormat('pt-BR').format(Number(value ?? 0));
const FormatQty = (value) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 6 }).format(Number(value ?? 0));

const InventoryAgingPanel = ({ label }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            setData(await GetAging());
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.field.code'),
                getValue: (row) => row.code || '',
                render: (row) => <span className="font-mono">{row.code}</span>,
            },
            {
                key: 'age_days',
                label: label('wms.aging.age_days'),
                getValue: (row) => row.age_days,
                render: (row) => <span className="font-semibold">{FormatInt(row.age_days)}</span>,
            },
            { key: 'bucket', label: label('wms.aging.bucket'), getValue: (row) => row.bucket || '' },
            {
                key: 'created_at',
                label: label('wms.lpn.field.created_at'),
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
            {
                key: 'location_code',
                label: label('wms.lpn.field.location'),
                getValue: (row) => row.location_code || '',
                render: (row) => <span className="font-mono">{row.location_code || '—'}</span>,
            },
            { key: 'deposit_code', label: label('wms.lpn.field.deposit'), getValue: (row) => row.deposit_code || '' },
            {
                key: 'total_qty',
                label: label('wms.aging.total_qty'),
                getValue: (row) => Number(row.total_qty ?? 0),
                render: (row) => FormatQty(row.total_qty),
            },
        ],
        [label],
    );

    if (loading && !data) {
        return <SkeletonTable columns={7} rows={8} />;
    }

    if (loadError) {
        return (
            <ErrorCard
                title={label('wms.aging.title')}
                message={loadError}
                action={label('wms.common.retry')}
                actionFn={load}
            />
        );
    }

    const summary = data?.summary ?? {};

    return (
        <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-xl border-2 border-info-400 bg-info-soft px-4 py-3 dark:border-info-700 dark:bg-info-900/30">
                <Info size={22} className="mt-0.5 shrink-0 text-info-text dark:text-info-300" />
                <p className="text-sm text-info-text dark:text-info-200">{label('wms.aging.warning_only')}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                    <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.aging.oldest')}</p>
                    <p className="mt-1 font-mono text-lg font-bold">{summary.oldest_code || '—'}</p>
                    <p className="text-xs text-mono-500">
                        {summary.oldest_age_days === null ? '—' : `${FormatInt(summary.oldest_age_days)} dias`}
                    </p>
                </div>
                <div className="rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                    <p className="text-xs uppercase tracking-wide text-mono-500">{label('wms.aging.average_age')}</p>
                    <p className="mt-1 text-2xl font-bold">
                        {summary.average_age_days === null ? '—' : FormatInt(summary.average_age_days)}
                    </p>
                </div>
                {(data?.buckets ?? []).slice(-2).map((bucket) => (
                    <div key={bucket.label} className="rounded-xl border border-mono-200 p-4 dark:border-mono-800">
                        <p className="text-xs uppercase tracking-wide text-mono-500">
                            {label('wms.aging.bucket')} {bucket.label}
                        </p>
                        <p className="mt-1 text-2xl font-bold">{FormatInt(bucket.lpn_count)}</p>
                    </div>
                ))}
            </div>

            {/* Histograma simples: a distribuição por faixa é o que responde
                "o galpão está girando?" antes de qualquer linha da tabela. */}
            <div className="flex flex-wrap gap-2">
                {(data?.buckets ?? []).map((bucket) => (
                    <div
                        key={bucket.label}
                        className="min-w-[110px] flex-1 rounded-lg border border-mono-200 px-3 py-2 dark:border-mono-800"
                    >
                        <p className="text-xs text-mono-500">{bucket.label} dias</p>
                        <p className="text-lg font-semibold">{FormatInt(bucket.lpn_count)}</p>
                        <p className="text-xs text-mono-500">{FormatQty(bucket.total_qty)}</p>
                    </div>
                ))}
            </div>

            <StandardTable
                rows={data?.rows ?? []}
                columns={columns}
                rowKey={(row) => row.id}
                countLabel={label('wms.aging.title')}
                exportFileName="wms-fifo-aging"
                emptyMessage={label('wms.aging.empty')}
            />
        </div>
    );
};

export default InventoryAgingPanel;
